import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container, Typography, Box, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Select, MenuItem, Button,
    Alert, CircularProgress, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Stack, Tooltip,
    useTheme, useMediaQuery, Divider, InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ServiceTier, UserStatus } from '../constants/enums';

const arborGreen = '#006D43';

// Memoized card for mobile view
const UserQuotaCard = React.memo(({
    user,
    currentUser,
    handleTierChange,
    handleOpenEditUsage,
    handleResetUsage,
    handleOpenRevokeSessions,
    handleOpenReset2FA,
    handleOpenStatusDialog,
    handleOpenDeleteUser,
    getTierChip,
    getStatusChip
}) => {
    const { t } = useTranslation();
    const scanQuota = user.quotas?.find(q => q.feature_key === 'ai_document_scan') || {};
    const adviceQuota = user.quotas?.find(q => q.feature_key === 'ai_advice') || {};
    const isSelf = currentUser && (currentUser.id === user.user_id || currentUser.email === user.email);
    const isSuperAdminUser = Boolean(user.is_super_admin);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {user.email} (ID: {user.user_id})
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {getStatusChip(user.status)}
                    {getTierChip(user.service_tier)}
                </Stack>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('Service Tier:')}
                </Typography>
                <Select
                    size="small"
                    value={user.service_tier}
                    onChange={(e) => handleTierChange(user.user_id, e.target.value)}
                    sx={{ minWidth: 100, height: 30, fontSize: '0.8rem', fontWeight: 600, borderRadius: 2 }}
                >
                    <MenuItem value="FREE">{t('FREE')}</MenuItem>
                    <MenuItem value="PRO">{t('PRO')}</MenuItem>
                    <MenuItem value="ADMIN">{t('ADMIN')}</MenuItem>
                </Select>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('Document Scans:')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {scanQuota.limit < 0 ? `${scanQuota.used || 0} ${t('used')} (${t('Unlimited')})` : `${scanQuota.used || 0} / ${scanQuota.limit}`}
                    </Typography>
                    <Tooltip title={t("Adjust Scan Quota")}>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenEditUsage(user, 'ai_document_scan')}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Reset Scan Quota")}>
                        <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleResetUsage(user.user_id, 'ai_document_scan')}
                        >
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('AI Advisor:')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {adviceQuota.limit < 0 ? `${adviceQuota.used || 0} ${t('used')} (${t('Unlimited')})` : `${adviceQuota.used || 0} / ${adviceQuota.limit}`}
                    </Typography>
                    <Tooltip title={t("Adjust Advisor Quota")}>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenEditUsage(user, 'ai_advice')}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Reset Advisor Quota")}>
                        <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleResetUsage(user.user_id, 'ai_advice')}
                        >
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Tooltip title={t("Revoke All Sessions")}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VpnKeyIcon fontSize="small" />}
                        onClick={() => handleOpenRevokeSessions(user)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        {t('Revoke Sessions')}
                    </Button>
                </Tooltip>
                <Tooltip title={t("Reset 2FA")}>
                    <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        startIcon={<LockResetIcon fontSize="small" />}
                        onClick={() => handleOpenReset2FA(user)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        {t('Reset 2FA')}
                    </Button>
                </Tooltip>
                {user.status === UserStatus.SUSPENDED ? (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                        onClick={() => handleOpenStatusDialog(user, UserStatus.ACTIVE)}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: arborGreen,
                            borderColor: arborGreen,
                            '&:hover': {
                                borderColor: arborGreen,
                                bgcolor: 'rgba(0, 109, 67, 0.08)'
                            }
                        }}
                    >
                        {t('Activate Account')}
                    </Button>
                ) : (
                    <Tooltip
                        title={
                            isSelf
                                ? t("You cannot modify your own status")
                                : isSuperAdminUser
                                ? t("Super admin status cannot be modified")
                                : t("Suspend Account")
                        }
                    >
                        <span>
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={isSelf || isSuperAdminUser}
                                startIcon={<BlockIcon fontSize="small" />}
                                onClick={() => handleOpenStatusDialog(user, UserStatus.SUSPENDED)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                {t('Suspend Account')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip
                    title={
                        isSelf
                            ? t("You cannot delete your own account")
                            : isSuperAdminUser
                            ? t("Super admin accounts cannot be deleted")
                            : t("Delete User")
                    }
                >
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            disabled={isSelf || isSuperAdminUser}
                            onClick={() => handleOpenDeleteUser(user)}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: isSelf || isSuperAdminUser ? undefined : '#401B1B',
                                color: isSelf || isSuperAdminUser ? undefined : '#401B1B',
                                '&:hover': {
                                    borderColor: '#2d1010',
                                    bgcolor: 'rgba(64, 27, 27, 0.08)'
                                }
                            }}
                        >
                            {t('Delete')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
        </Paper>
    );
});

const AdminQuota = () => {
    const { t } = useTranslation();
    const { currentUser, api } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = useState([]);
    const [featureConfigs, setFeatureConfigs] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Search, Tier, and Status Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [tierFilter, setTierFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Edit usage dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState('ai_document_scan');
    const [newUsageCount, setNewUsageCount] = useState(0);
    const [updatingUsage, setUpdatingUsage] = useState(false);

    // Delete user dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
    const [deletingUser, setDeletingUser] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Status change dialog state
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [userToChangeStatus, setUserToChangeStatus] = useState(null);
    const [targetStatus, setTargetStatus] = useState(UserStatus.ACTIVE);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [statusError, setStatusError] = useState(null);

    // Revoke sessions dialog state
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [userToRevokeSessions, setUserToRevokeSessions] = useState(null);
    const [revokingSessions, setRevokingSessions] = useState(false);
    const [revokeError, setRevokeError] = useState(null);

    // Reset 2FA dialog state
    const [reset2FADialogOpen, setReset2FADialogOpen] = useState(false);
    const [userToReset2FA, setUserToReset2FA] = useState(null);
    const [resetting2FA, setResetting2FA] = useState(false);
    const [reset2FAError, setReset2FAError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [usersRes, featuresRes] = await Promise.all([
                api.get('/admin/quota/users'),
                api.get('/admin/quota/features')
            ]);
            setUsers(usersRes.data || []);
            setFeatureConfigs(featuresRes.data || {});
        } catch (err) {
            console.error("Failed to fetch admin quota data", err);
            setError(err.response?.data?.detail || "Could not load quota data.");
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (currentUser && !currentUser.is_super_admin) {
            navigate('/');
            return;
        }

        if (currentUser?.is_super_admin) {
            fetchData();
        }
    }, [currentUser, navigate, fetchData]);

    // Dynamic search, tier & status filtering
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesTier = tierFilter === 'ALL' || user.service_tier === tierFilter;
            if (!matchesTier) return false;

            const userStatus = user.status || UserStatus.ACTIVE;
            const matchesStatus = statusFilter === 'ALL' || userStatus === statusFilter;
            if (!matchesStatus) return false;

            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase().trim();
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
            const email = (user.email || '').toLowerCase();
            const userId = String(user.user_id || '');

            return fullName.includes(query) || email.includes(query) || userId.includes(query);
        });
    }, [users, tierFilter, statusFilter, searchQuery]);

    const handleTierChange = async (userId, newTier) => {
        setError(null);
        setSuccessMsg(null);
        try {
            const response = await api.put(`/admin/quota/users/${userId}/tier`, {
                service_tier: newTier
            });
            setUsers(prev => prev.map(u => u.user_id === userId ? response.data : u));
            setSuccessMsg(`User service tier updated to ${newTier}`);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to update user tier.");
        }
    };

    const handleResetUsage = async (userId, featureKey = 'ai_document_scan') => {
        setError(null);
        setSuccessMsg(null);
        try {
            await api.delete(`/admin/quota/users/${userId}/usage/${featureKey}`);
            await fetchData();
            const featLabel = featureKey === 'ai_advice' ? t('AI Advisor') : t('Document scan');
            setSuccessMsg(t('{{feat}} usage reset to 0 for user #{{id}}', { feat: featLabel, id: userId }));
        } catch (err) {
            setError(err.response?.data?.detail || t("Failed to reset user usage."));
        }
    };

    const handleOpenEditUsage = (user, featureKey = 'ai_document_scan') => {
        setSelectedUser(user);
        setSelectedFeature(featureKey);
        const quota = user.quotas?.find(q => q.feature_key === featureKey);
        setNewUsageCount(quota ? quota.used : 0);
        setEditDialogOpen(true);
    };

    const handleSaveUsage = async () => {
        if (!selectedUser) return;
        setUpdatingUsage(true);
        setError(null);
        setSuccessMsg(null);
        try {
            await api.put(`/admin/quota/users/${selectedUser.user_id}/usage/${selectedFeature}`, {
                usage_count: parseInt(newUsageCount, 10) || 0
            });
            setEditDialogOpen(false);
            await fetchData();
            const featLabel = selectedFeature === 'ai_advice' ? t('AI Advisor') : t('Document scan');
            setSuccessMsg(t('Updated {{feat}} usage to {{count}} for user #{{id}}', { feat: featLabel, count: newUsageCount, id: selectedUser.user_id }));
        } catch (err) {
            setError(err.response?.data?.detail || t("Failed to update usage count."));
        } finally {
            setUpdatingUsage(false);
        }
    };

    const handleOpenDeleteUser = (user) => {
        setUserToDelete(user);
        setDeleteConfirmEmail('');
        setDeleteError(null);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteUser = async () => {
        if (!userToDelete || deleteConfirmEmail !== userToDelete.email) return;
        setDeletingUser(true);
        setDeleteError(null);
        setError(null);
        setSuccessMsg(null);
        try {
            const res = await api.delete(`/admin/quota/users/${userToDelete.user_id}`);
            setUsers(prev => prev.filter(u => u.user_id !== userToDelete.user_id));
            setSuccessMsg(res.data?.message || t('User deleted successfully'));
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        } catch (err) {
            setDeleteError(err.response?.data?.detail || t('Failed to delete user.'));
        } finally {
            setDeletingUser(false);
        }
    };

    const handleOpenStatusDialog = (user, newStatus) => {
        setUserToChangeStatus(user);
        setTargetStatus(newStatus);
        setStatusError(null);
        setStatusDialogOpen(true);
    };

    const handleConfirmStatusChange = async () => {
        if (!userToChangeStatus || !targetStatus) return;
        setUpdatingStatus(true);
        setStatusError(null);
        setError(null);
        setSuccessMsg(null);
        try {
            const response = await api.put(`/admin/quota/users/${userToChangeStatus.user_id}/status`, {
                status: targetStatus
            });
            setUsers(prev => prev.map(u => u.user_id === userToChangeStatus.user_id ? { ...u, ...response.data } : u));
            setSuccessMsg(t('User status updated to {{status}}', { status: targetStatus }));
            setStatusDialogOpen(false);
            setUserToChangeStatus(null);
        } catch (err) {
            setStatusError(err.response?.data?.detail || t('Failed to update user status.'));
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleOpenRevokeSessions = (user) => {
        setUserToRevokeSessions(user);
        setRevokeError(null);
        setRevokeDialogOpen(true);
    };

    const handleConfirmRevokeSessions = async () => {
        if (!userToRevokeSessions) return;
        setRevokingSessions(true);
        setRevokeError(null);
        setError(null);
        setSuccessMsg(null);
        try {
            const response = await api.post(`/admin/quota/users/${userToRevokeSessions.user_id}/revoke-sessions`);
            setUsers(prev => prev.map(u => u.user_id === userToRevokeSessions.user_id ? { ...u, token_version: response.data.token_version } : u));
            setSuccessMsg(t('All active sessions revoked for {{email}}', { email: userToRevokeSessions.email }));
            setRevokeDialogOpen(false);
            setUserToRevokeSessions(null);
        } catch (err) {
            setRevokeError(err.response?.data?.detail || t('Failed to revoke sessions.'));
        } finally {
            setRevokingSessions(false);
        }
    };

    const handleOpenReset2FA = (user) => {
        setUserToReset2FA(user);
        setReset2FAError(null);
        setReset2FADialogOpen(true);
    };

    const handleConfirmReset2FA = async () => {
        if (!userToReset2FA) return;
        setResetting2FA(true);
        setReset2FAError(null);
        setError(null);
        setSuccessMsg(null);
        try {
            const response = await api.post(`/admin/quota/users/${userToReset2FA.user_id}/reset-2fa`);
            setUsers(prev => prev.map(u => u.user_id === userToReset2FA.user_id ? { ...u, token_version: response.data.token_version } : u));
            setSuccessMsg(t('2FA reset successfully for {{email}}', { email: userToReset2FA.email }));
            setReset2FADialogOpen(false);
            setUserToReset2FA(null);
        } catch (err) {
            setReset2FAError(err.response?.data?.detail || t('Failed to reset 2FA.'));
        } finally {
            setResetting2FA(false);
        }
    };

    const getTierChip = (tier) => {
        if (tier === ServiceTier.ADMIN) return <Chip label={t('ADMIN')} color="secondary" size="small" sx={{ fontWeight: 700 }} />;
        if (tier === ServiceTier.PRO) return <Chip label={t('PRO')} color="primary" size="small" sx={{ fontWeight: 700, bgcolor: arborGreen }} />;
        return <Chip label={t('FREE')} size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    };

    const getStatusChip = (status) => {
        const currentStatus = status || UserStatus.ACTIVE;
        if (currentStatus === UserStatus.SUSPENDED) {
            return (
                <Chip
                    label={t('SUSPENDED')}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                        color: 'error.main',
                        border: '1px solid rgba(211, 47, 47, 0.2)',
                        fontWeight: 700
                    }}
                />
            );
        }
        if (currentStatus === UserStatus.DEACTIVATED) {
            return (
                <Chip
                    label={t('DEACTIVATED')}
                    size="small"
                    variant="outlined"
                    sx={{
                        fontWeight: 600,
                        color: 'text.secondary'
                    }}
                />
            );
        }
        return (
            <Chip
                label={t('ACTIVE')}
                size="small"
                sx={{
                    bgcolor: 'rgba(0, 109, 67, 0.08)',
                    color: arborGreen,
                    border: '1px solid rgba(0, 109, 67, 0.2)',
                    fontWeight: 700
                }}
            />
        );
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!currentUser?.is_super_admin) {
        return (
            <Container maxWidth="lg" sx={{ mt: 6 }}>
                <Alert severity="error">{t('Super admin access required.')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }} color="primary">
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: arborGreen, letterSpacing: '-0.02em' }}>
                        {t('User Management')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Manage user accounts, security settings, service tiers, and feature limits.')}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{successMsg}</Alert>}

            {/* Feature Config Summary */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AutoAwesomeIcon sx={{ color: arborGreen }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('Feature Limits Overview')}
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    {Object.entries(featureConfigs).map(([featureKey, config]) => (
                        <Box key={featureKey} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: arborGreen }}>
                                {featureKey}
                            </Typography>
                            {Object.entries(config).map(([tier, cfg]) => (
                                <Typography key={tier} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                    <span><strong>{tier}:</strong></span>
                                    <span>{cfg.limit < 0 ? t('Unlimited') : `${cfg.limit} / ${cfg.reset_policy}`}</span>
                                </Typography>
                            ))}
                        </Box>
                    ))}
                </Stack>
            </Paper>

            {/* User Quotas Table Container */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                {/* Search & Filter Toolbar */}
                <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {t('User Accounts')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('Showing {{filtered}} of {{total}} users', { filtered: filteredUsers.length, total: users.length })}
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            size="small"
                            placeholder={t("Search users by name, email, or ID...")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: '100%', sm: 160 } }}>
                                <Select
                                    size="small"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    fullWidth
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="ALL">{t('All Statuses')}</MenuItem>
                                    <MenuItem value={UserStatus.ACTIVE}>{t('ACTIVE')}</MenuItem>
                                    <MenuItem value={UserStatus.SUSPENDED}>{t('SUSPENDED')}</MenuItem>
                                    <MenuItem value={UserStatus.DEACTIVATED}>{t('DEACTIVATED')}</MenuItem>
                                </Select>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: '100%', sm: 160 } }}>
                                <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                <Select
                                    size="small"
                                    value={tierFilter}
                                    onChange={(e) => setTierFilter(e.target.value)}
                                    fullWidth
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="ALL">{t('All Tiers')}</MenuItem>
                                    <MenuItem value="FREE">{t('FREE')}</MenuItem>
                                    <MenuItem value="PRO">{t('PRO')}</MenuItem>
                                    <MenuItem value="ADMIN">{t('ADMIN')}</MenuItem>
                                </Select>
                            </Box>
                        </Stack>
                    </Stack>
                </Box>

                {filteredUsers.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("No users match the search criteria.")}
                        </Typography>
                    </Box>
                ) : isMobile ? (
                    <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                        {filteredUsers.map((user) => (
                            <UserQuotaCard
                                key={user.user_id}
                                user={user}
                                currentUser={currentUser}
                                handleTierChange={handleTierChange}
                                handleOpenEditUsage={handleOpenEditUsage}
                                handleResetUsage={handleResetUsage}
                                handleOpenRevokeSessions={handleOpenRevokeSessions}
                                handleOpenReset2FA={handleOpenReset2FA}
                                handleOpenStatusDialog={handleOpenStatusDialog}
                                handleOpenDeleteUser={handleOpenDeleteUser}
                                getTierChip={getTierChip}
                                getStatusChip={getStatusChip}
                            />
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('ID')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('User')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Status')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Service Tier')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('AI Quota Usage')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">{t('Actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map((user) => {
                                    const scanQuota = user.quotas?.find(q => q.feature_key === 'ai_document_scan') || {};
                                    const adviceQuota = user.quotas?.find(q => q.feature_key === 'ai_advice') || {};
                                    const isSelf = currentUser && (currentUser.id === user.user_id || currentUser.email === user.email);
                                    const isSuperAdminUser = Boolean(user.is_super_admin);

                                    return (
                                        <TableRow key={user.user_id} hover>
                                            <TableCell>{user.user_id}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {user.first_name} {user.last_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {user.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(user.status)}
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    size="small"
                                                    value={user.service_tier}
                                                    onChange={(e) => handleTierChange(user.user_id, e.target.value)}
                                                    sx={{ minWidth: 110, fontWeight: 600, borderRadius: 2 }}
                                                >
                                                    <MenuItem value="FREE">{t('FREE')}</MenuItem>
                                                    <MenuItem value="PRO">{t('PRO')}</MenuItem>
                                                    <MenuItem value="ADMIN">{t('ADMIN')}</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 50 }}>
                                                            {t('Scans:')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {scanQuota.limit < 0 ? `${scanQuota.used || 0} ${t('used')} (${t('Unlimited')})` : `${scanQuota.used || 0} / ${scanQuota.limit}`}
                                                        </Typography>
                                                        <Tooltip title={t("Adjust Scan Quota")}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenEditUsage(user, 'ai_document_scan')}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t("Reset Scan Quota")}>
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => handleResetUsage(user.user_id, 'ai_document_scan')}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <RestartAltIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 50 }}>
                                                            {t('Advisor:')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {adviceQuota.limit < 0 ? `${adviceQuota.used || 0} ${t('used')} (${t('Unlimited')})` : `${adviceQuota.used || 0} / ${adviceQuota.limit}`}
                                                        </Typography>
                                                        <Tooltip title={t("Adjust Advisor Quota")}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenEditUsage(user, 'ai_advice')}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t("Reset Advisor Quota")}>
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => handleResetUsage(user.user_id, 'ai_advice')}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <RestartAltIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    <Tooltip title={t("Revoke All Sessions")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenRevokeSessions(user)}
                                                        >
                                                            <VpnKeyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t("Reset 2FA")}>
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleOpenReset2FA(user)}
                                                        >
                                                            <LockResetIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {user.status === UserStatus.SUSPENDED ? (
                                                        <Tooltip title={t("Activate Account")}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenStatusDialog(user, UserStatus.ACTIVE)}
                                                                sx={{ color: arborGreen }}
                                                            >
                                                                <CheckCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip
                                                            title={
                                                                isSelf
                                                                    ? t("You cannot modify your own status")
                                                                    : isSuperAdminUser
                                                                    ? t("Super admin status cannot be modified")
                                                                    : t("Suspend Account")
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={isSelf || isSuperAdminUser}
                                                                    onClick={() => handleOpenStatusDialog(user, UserStatus.SUSPENDED)}
                                                                >
                                                                    <BlockIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip
                                                        title={
                                                            isSelf
                                                                ? t("You cannot delete your own account")
                                                                : isSuperAdminUser
                                                                ? t("Super admin accounts cannot be deleted")
                                                                : t("Delete User")
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isSelf || isSuperAdminUser}
                                                                onClick={() => handleOpenDeleteUser(user)}
                                                                sx={{
                                                                    color: isSelf || isSuperAdminUser ? 'text.disabled' : '#401B1B',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(64, 27, 27, 0.08)'
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Edit Usage Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {selectedFeature === 'ai_advice' ? t("Update Advisor Usage Count") : t("Update Scan Usage Count")}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {selectedFeature === 'ai_advice'
                                ? t("Manually set the current AI advisor usage count for")
                                : t("Manually set the current scan usage count for")
                            }{' '}
                            <strong>{selectedUser?.email}</strong>.
                        </Typography>
                        <TextField
                            autoFocus
                            label={t("Usage Count")}
                            type="number"
                            fullWidth
                            size="small"
                            value={newUsageCount}
                            onChange={(e) => setNewUsageCount(e.target.value)}
                            inputProps={{ min: 0, inputMode: 'numeric' }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={updatingUsage} sx={{ textTransform: 'none', fontWeight: 600 }}>{t("Cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveUsage}
                        disabled={updatingUsage}
                        startIcon={updatingUsage ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ bgcolor: arborGreen, color: '#fff', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                        {updatingUsage ? t('Saving...') : t('Save Usage')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={deletingUser ? undefined : () => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#401B1B', pb: 1 }}>
                    {t("Delete User Account")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {t("Warning: This action is permanent and cannot be undone. All user data and solely-owned ledgers will be permanently deleted.")}
                        </Alert>
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {userToDelete?.first_name} {userToDelete?.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {userToDelete?.email} (ID: {userToDelete?.user_id})
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            {t("To confirm deletion, please type the user's email:")} <strong>{userToDelete?.email}</strong>
                        </Typography>
                        <TextField
                            autoFocus
                            size="small"
                            fullWidth
                            value={deleteConfirmEmail}
                            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                            placeholder={userToDelete?.email}
                            disabled={deletingUser}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 }
                            }}
                        />
                        {deleteError && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {deleteError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deletingUser}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteUser}
                        disabled={deletingUser || !userToDelete || deleteConfirmEmail !== userToDelete.email}
                        startIcon={deletingUser ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                        sx={{
                            bgcolor: '#401B1B',
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#2d1010' }
                        }}
                    >
                        {deletingUser ? t('Deleting...') : t('Delete Permanently')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Sessions Dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={revokingSessions ? undefined : () => setRevokeDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    {t("Revoke All Sessions")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            {t('Are you sure you want to revoke all active sessions for {{email}}? The user will be immediately logged out across all devices.', { email: userToRevokeSessions?.email })}
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {userToRevokeSessions?.first_name} {userToRevokeSessions?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userToRevokeSessions?.email} (ID: {userToRevokeSessions?.user_id})
                            </Typography>
                        </Box>
                        {revokeError && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {revokeError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setRevokeDialogOpen(false)}
                        disabled={revokingSessions}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmRevokeSessions}
                        disabled={revokingSessions}
                        startIcon={revokingSessions ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2
                        }}
                    >
                        {revokingSessions ? t('Revoking...') : t('Revoke Sessions')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset 2FA Dialog */}
            <Dialog
                open={reset2FADialogOpen}
                onClose={resetting2FA ? undefined : () => setReset2FADialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    {t("Reset 2-Factor Authentication")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            {t('Are you sure you want to reset 2FA for {{email}}? This will remove all authenticator app and email verification methods and invalidate existing sessions.', { email: userToReset2FA?.email })}
                        </Alert>
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {userToReset2FA?.first_name} {userToReset2FA?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userToReset2FA?.email} (ID: {userToReset2FA?.user_id})
                            </Typography>
                        </Box>
                        {reset2FAError && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {reset2FAError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setReset2FADialogOpen(false)}
                        disabled={resetting2FA}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmReset2FA}
                        disabled={resetting2FA}
                        startIcon={resetting2FA ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2
                        }}
                    >
                        {resetting2FA ? t('Resetting...') : t('Reset 2FA')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Status Change Dialog */}
            <Dialog
                open={statusDialogOpen}
                onClose={updatingStatus ? undefined : () => setStatusDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1, color: targetStatus === UserStatus.SUSPENDED ? 'error.main' : arborGreen }}>
                    {targetStatus === UserStatus.SUSPENDED ? t("Suspend Account") : t("Activate Account")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {targetStatus === UserStatus.SUSPENDED ? (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {t('Suspending {{email}} will immediately block login and terminate all active sessions.', { email: userToChangeStatus?.email })}
                            </Alert>
                        ) : (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                {t('Activating {{email}} will restore their ability to log in.', { email: userToChangeStatus?.email })}
                            </Alert>
                        )}
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {userToChangeStatus?.first_name} {userToChangeStatus?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userToChangeStatus?.email} (ID: {userToChangeStatus?.user_id})
                            </Typography>
                        </Box>
                        {statusError && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {statusError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setStatusDialogOpen(false)}
                        disabled={updatingStatus}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmStatusChange}
                        disabled={updatingStatus}
                        startIcon={updatingStatus ? <CircularProgress size={16} color="inherit" /> : (targetStatus === UserStatus.SUSPENDED ? <BlockIcon /> : <CheckCircleOutlineIcon />)}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            bgcolor: targetStatus === UserStatus.SUSPENDED ? 'error.main' : arborGreen,
                            color: '#fff',
                            '&:hover': {
                                bgcolor: targetStatus === UserStatus.SUSPENDED ? 'error.dark' : '#005434'
                            }
                        }}
                    >
                        {updatingStatus
                            ? t('Updating...')
                            : targetStatus === UserStatus.SUSPENDED
                            ? t('Suspend Account')
                            : t('Activate Account')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminQuota;
